import { add, format } from 'date-fns';
import { useAlert } from 'src/components/alert';
import { CALLS_PER_SECOND } from 'src/constants/misc';
import { CALLING_TASK_RESULTS, VISITING_TASK_RESULTS } from 'src/constants/task_results';

export const getValidEventStausesForTask = (eventStatuses, taskTypes, taskTypeId) => {
  if (!taskTypeId) {
    return [];
  }
  const selectedTaskType = taskTypes?.find((task) => task.t_type_id === taskTypeId);
  if (selectedTaskType && eventStatuses) {
    const validEventStatusCodes =
      selectedTaskType.code === 'calling' ? CALLING_TASK_RESULTS : VISITING_TASK_RESULTS;
    return eventStatuses.filter((status) => validEventStatusCodes.includes(status.code));
  }
  return [];
};

export const getTaskEventStatuses = (task) => {
  if (task?.tasks_tasks_event_statuses) {
    return task.tasks_tasks_event_statuses
      .map((item) => item.tasks_event_statuses_event_status)
      .map((status) => ({
        label: status.name,
        value: status.code,
        event_status_id: status.event_status_id,
      }));
  }
  return [];
};

export const getApproximateCompletionTimeForVoiceRecordingTasks = (count) => {
  const completionTime = add(new Date(), { seconds: count * CALLS_PER_SECOND, hours: 1 });
  const isToday = new Date().getDate() === completionTime.getDate();
  return format(completionTime, isToday ? 'hh:00a (z)' : 'MMM dd, yyyy - hh:00a (z)');
};

export const useConfirmAndSendMessages = () => {
  const alert = useAlert();
  const getCostString = (cost, count) =>
    cost * count >= 0.5 ? (cost * (count ?? 0)).toFixed(3) : '0.50 (minimum cost for task)';

  const confirmAndSendMessages = (count, cost, action, target, item = 'SMS', send_at) =>
    alert.confirm({
      title: 'Confirm Sending Messages',
      description: `Are you sure you want to submit the task and send the ${item} to ${count} ${target}? You may be redirected to the customer portal if you haven't setup a default payment method`,
      subtextHTML: `
  <div style="margin-top:0.25rem;font-weight:500">Estimated Cost: $${getCostString(
    cost,
    count
  )}</div>
  <div style="font-size:0.75rem;color:#637381;margin-top:0.5rem;font-weight:400">Unsubscribe messages and other Outbound Messages will be billed additionally.</div>
      ${
        send_at
          ? `<div style="margin-top:0.25rem">Scheduled at: ${format(
              send_at,
              'MMM dd, yyyy - hh:mma'
            )}</div> `
          : ''
      }
  <div style="font-size:0.75rem;color:#637381;margin-top:1rem;font-weight:400">By clicking ${action}, you confirm that your text message follows all required carrier, state
    and federal rules regarding text messaging. You assume all legal responsibility for the message being sent. Please review RightInsight 
    <a href="https://rightimpact.org/terms" target="_blank" rel="noreferrer">
      Terms and Conditions page
    </a>
  for more information.</div>`,
      confirmationText: action,
    });

  const confirmSuspendSurveyExecutions = (phone, count) =>
    alert.confirm({
      title: 'Confirm Suspending Currently Running Surveys',
      description: `Are you sure you want to reassign a new survey to ${phone}?`,
      subtextHTML: `<div style="font-size:0.75rem;color:#637381;margin-top:1rem;font-weight:400">
        This will suspend execution of ${count} pending surveys to this phone number
      </div>`,
      confirmationText: 'Confirm',
    });

  const confirmRestartTask = () =>
    alert.confirm({
      title: 'Confirm Restarting Task',
      description: `Are you sure you want to restart processing?`,
      subtextHTML: `<div style="font-size:0.75rem;color:#637381;margin-top:1rem;font-weight:400">
            Please note that this will NOT reset progress and WILL NOT send the message to people who have received it already.
            Use this only if the task progress is not complete and hasn’t updated in a while.
          </div>`,
      confirmationText: 'Confirm',
    });

  const confirmCancelTask = (items) =>
    alert.confirm({
      title: 'Confirm Canceling Task',
      description: `Are you sure you want to cancel this task?`,
      subtextHTML: `<div style="font-size:0.75rem;color:#637381;margin-top:1rem;font-weight:400">
              This will cancel pending outgoing ${items}.
            </div>`,
      confirmationText: 'Confirm',
    });

  const confirmAndSendVoiceRecordings = (count, cost, durationInMins) =>
    alert.confirm({
      title: 'Confirm Sending Landline Voicemails',
      description: `Are you sure you want to submit the task and send voice recordings (${durationInMins} mins each) to ${count} voters? You may be redirected to the customer portal if you haven't setup a default payment method`,
      subtextHTML: `
    <div style="margin-top:0.25rem;font-weight:500">Estimated Cost: $${getCostString(
      cost,
      count
    )}</div>
    <p>This task will be complete by approximately ${getApproximateCompletionTimeForVoiceRecordingTasks(
      count
    )}</p>
    <div style="font-size:0.75rem;color:#637381;margin-top:1rem;font-weight:400">
      By clicking Create Task, you confirm that your voice recordings follows all required
      carrier, state and federal rules regarding voice recordings. You assume all legal
      responsibility for the message being sent. Please review RightInsight 
      <a href="https://rightimpact.org/terms" target="_blank" rel="noreferrer">Terms and Conditions page</a> for more information
    </div>`,
      confirmationText: 'Create Task',
    });

  const cannotSendVoiceRecording = () =>
    alert.confirm({
      title: 'Voice recording task cannot be created',
      subtextHTML: `
      <div style="margin-top:0.25rem;font-weight:500">Not all calls will go out by 8 PM. Please create this task early tomorrow.</div>
      </div>`,
      confirmationText: 'Close',
      showCancelButton: false,
    });

  return {
    confirmAndSendMessages,
    confirmSuspendSurveyExecutions,
    confirmRestartTask,
    confirmCancelTask,
    confirmAndSendVoiceRecordings,
    cannotSendVoiceRecording,
  };
};

export const useBulkTasksHelper = () => {
  const alert = useAlert();
  const alertLargeWalkListDetected = () =>
    alert.dialog({
      title: 'Large Walk List Detected',
      subtextHTML: `<div style="color:#637381;margin-top:1rem;font-weight:400">
      For larger walk lists, we recommend splitting the task into multiple smaller tasks. You can split the task into multiple smaller lists based on geography or limit the number of homes per list.
      </div>`,
      confirmationText: 'OK',
      showCancelButton: false,
    });

  const confirmBulkCreation = (
    noOfTasks,
    isWalkingTask,
    bulkTaskSplitConfig,
    isRegeneration = false
  ) => {
    const isSplitByHomes = bulkTaskSplitConfig?.splitMethod === 'numberOfHomes';
    const listType = isWalkingTask ? 'walk' : 'call';

    return alert.confirm({
      title: isRegeneration
        ? `Confirm Regenerating ${isWalkingTask ? 'Walk' : 'Call'} Lists`
        : `Confirm Multiple ${isWalkingTask ? 'Walk' : 'Call'} Lists`,
      description: isRegeneration
        ? `All individual ${listType} lists will be regenerated and any existing progress will be reset.`
        : `You are about to create ${noOfTasks} ${listType} lists.`,
      subtextHTML: `<div style="font-size:0.75rem;color:#637381;margin-top:1rem;font-weight:400">
        ${
          !isRegeneration
            ? `
        <div style="margin-bottom:0.5rem">
          ${
            isSplitByHomes
              ? `<strong>Important:</strong> Individual tasks cannot be edited after creation. Only team member assignments can be modified at the individual task level.`
              : `<strong>Important:</strong> Individual tasks can be edited after creation. Team member assignments can also be modified at the individual task level.`
          }
        </div>`
            : ''
        }
        <div style="margin-bottom:0.5rem">
          Other settings (demographics, filters, etc.) can be changed by editing the bulk task, which will regenerate all individual ${listType} lists.
        </div>
        ${
          isSplitByHomes
            ? `<div style="margin-bottom:0.5rem">
                <strong>Note:</strong> When splitting by number of homes, some ${listType} lists may have fewer homes than the selected amount, as homes that are far away from each other may be grouped into separate lists for efficiency.
              </div>`
            : ''
        }
        <div>Please review all task details carefully before confirming.</div>
      </div>`,
      confirmationText: 'Confirm',
      showCancelButton: true,
      cancellationText: 'Go back',
    });
  };

  return {
    alertLargeWalkListDetected,
    confirmBulkCreation,
  };
};
